import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useBrand } from '~/modules/branding';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { LabelListItem } from '~settings/api/lib/schemas';
import type { MenuItemState } from '~settings/components/ActionsMenu';
import { ActionsMenu } from '~settings/components/ActionsMenu';
import { GroupInfo } from '~settings/components/GroupInfo';
import { LabelName } from '~settings/modules/label/components/LabelName';

export const useLabelsColumns = (
  onDelete: (id: string, name: string) => void,
) => {
  const { hasPermission } = useUser();
  const brand = useBrand();
  const isCurrentUserExternal = useIsExternalUser();
  const [isManageLabelEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsManageLabel,
  ]);

  const { t } = useTranslation('settings');

  return useMemo(() => {
    const columnHelper = createColumnHelper<LabelListItem>();

    const sublabelsColumns = {
      name: columnHelper.accessor('name', {
        id: 'name',
        header: () => t('label.titles'),
        cell: (cell) => <LabelName name={cell.getValue()} />,
        enableSorting: true,
      }),
      labelType: columnHelper.accessor('isParentLabel', {
        id: 'type',
        header: () => t('labelType'),
        cell: (cell) => t(cell.getValue() ? 'parentLabel' : 'sublabel'),
        enableSorting: true,
      }),
      labelGroup: columnHelper.accessor((row) => row.labelGroups, {
        id: 'labelGroups',
        header: () => t('labelGroup.list'),
        cell: (cell) => (
          <GroupInfo groups={(cell.getValue() ?? []).map(({ name }) => name)} />
        ),
        enableSorting: false,
      }),
      usersCount: columnHelper.accessor('usersCount', {
        id: 'usersCount',
        header: () => t('users'),
        cell: (cell) => (cell.getValue() ? cell.getValue() : '-'),
        enableSorting: true,
      }),
      sublabelsCount: columnHelper.accessor('sublabelsCount', {
        id: 'sublabelsCount',
        header: () => t('sublabels'),
        cell: (cell) => (cell.getValue() ? cell.getValue() : '-'),
        enableSorting: true,
      }),
      controls: columnHelper.display({
        id: 'actions',
        cell: ({ row }) => {
          const { id, name } = row.original;
          const actions = [];
          const canEditLabel = hasPermission('LABEL.EDIT', {
            id: brand.name,
            type: 'APPLICATION',
          });
          const canDeleteLabel = hasPermission('LABEL.DELETE', {
            id: brand.name,
            type: 'APPLICATION',
          });

          if (canEditLabel) {
            actions.push({
              id: 'edit',
              label: (
                <Link to={SETTINGS_ROUTES.LABEL_EDIT(id)}>
                  {t('label.editLink')}
                </Link>
              ),
            });
          }

          if (canDeleteLabel) {
            actions.push({
              id: 'delete',
              label: t('label.deleteAction'),
              state: 'error' as MenuItemState,
              callback: () => onDelete(id, name),
            });
          }

          return <ActionsMenu actions={actions} />;
        },
      }),
    };

    let columnsOrder: (keyof typeof sublabelsColumns)[] = [];

    if (isCurrentUserExternal) {
      columnsOrder = ['name', 'labelType', 'usersCount', 'sublabelsCount'];
    } else {
      columnsOrder = [
        'name',
        'labelType',
        'labelGroup',
        'usersCount',
        'sublabelsCount',
      ];
    }

    if (isManageLabelEnabled) {
      columnsOrder.push('controls');
    }

    return columnsOrder.map((key) => sublabelsColumns[key]);
  }, [
    brand.name,
    hasPermission,
    isCurrentUserExternal,
    isManageLabelEnabled,
    onDelete,
    t,
  ]);
};
